import { useCallback, useEffect, useRef, useState } from 'react';
import { 
  compressEventData, 
  smartCleanup, 
  calculateMemoryStats,
} from '@/utils/memoryManager';
import { ITimelineEvent, IMemoryStats, TEventType, MEMORY_CONSTANTS } from '@/types/timeline';

interface UserAction {
  actionType: string;
  element: string;
  coordinates: { x: number; y: number };
  target: {
    id: string;
    className: string;
    tagName: string;
    textContent?: string;
  };
  value?: any;
  timestamp: number;
  url: string;
  clientId?: string;
}

interface StateChange {
  componentName?: string;
  prevState?: any;
  newState: any;
  props?: any;
  stackTrace?: string[];
  timestamp: number;
  url: string;
  clientId?: string;
}

interface ErrorData {
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  timestamp: number;
  url: string;
  userAgent: string;
  errorInfo?: any;
  clientId?: string;
}

interface ConnectedClient {
  id: string;
  connectedAt: Date;
  lastActivity: Date;
  url?: string;
  userAgent?: string;
}

interface DebugEvent {
  id: string;
  type: 'USER_ACTION' | 'STATE_CHANGE' | 'ERROR' | 'CONNECTION' | 'API_CALL' | 'NAVIGATION' | 'PERFORMANCE';
  timestamp: number;
  clientId: string;
  data: UserAction | StateChange | ErrorData | any;
  title: string;
  description: string;
  isError?: boolean;
  severity?: 'critical' | 'warning' | 'info';
  isBookmarked?: boolean;
  suggestion?: string;
  stateChanges?: any[];
  memorySize?: number;
  isCompressed?: boolean;
  originalDataSize?: number;
}

interface UseServerConnectionReturn {
  isConnected: boolean;
  connectedClients: ConnectedClient[];
  events: ITimelineEvent[];
  connectionError: string | null;
  memoryStats: IMemoryStats;
  reconnect: () => void;
  clearEvents: () => void;
  updateEvents: (newEvents: ITimelineEvent[]) => void;
}

const WEBSOCKET_URL = 'ws://localhost:4000/ws'; // 3001 → 4000으로 변경

// DebugEvent를 ITimelineEvent로 변환하는 헬퍼 함수
const convertToTimelineEvent = (debugEvent: DebugEvent): ITimelineEvent => {
  let eventType: TEventType;
  
  switch (debugEvent.type) {
    case 'USER_ACTION':
      eventType = 'user_action';
      break;
    case 'STATE_CHANGE':
      eventType = 'state_change';
      break;
    case 'ERROR':
      eventType = 'error';
      break;
    case 'CONNECTION':
      eventType = 'connection';
      break;
    case 'API_CALL':
      eventType = 'api_call';
      break;
    case 'NAVIGATION':
      eventType = 'navigation';
      break;
    case 'PERFORMANCE':
      eventType = 'performance';
      break;
    default:
      eventType = 'action';
  }

  // 데이터 구조 보강
  let enhancedData = { ...debugEvent.data };
  
  if (debugEvent.type === 'STATE_CHANGE') {
    enhancedData = {
      ...enhancedData,
      componentName: debugEvent.data?.componentName || 
                     extractComponentName(debugEvent.title) || 
                     'UnknownComponent',
      prevState: debugEvent.data?.prevState || null,
      newState: debugEvent.data?.newState || debugEvent.data || {},
      props: debugEvent.data?.props || {}
    };
  }
  
  if (debugEvent.type === 'API_CALL') {
    enhancedData = {
      ...enhancedData,
      method: debugEvent.data?.method || 'GET',
      url: debugEvent.data?.url || 'unknown',
      status: debugEvent.data?.status || debugEvent.data?.statusCode || 200,
      duration: debugEvent.data?.duration || debugEvent.data?.responseTime || 
                Math.floor(Math.random() * 500) + 100,
      success: debugEvent.data?.success !== false && !debugEvent.isError,
      responseData: debugEvent.data?.response || debugEvent.data?.responseData
    };
  }
  
  if (debugEvent.type === 'USER_ACTION') {
    enhancedData = {
      ...enhancedData,
      actionType: debugEvent.data?.actionType || extractActionType(debugEvent.title),
      element: debugEvent.data?.element || debugEvent.data?.target?.tagName || 'unknown',
      target: debugEvent.data?.target || {
        id: 'unknown',
        className: '',
        tagName: 'BUTTON'
      }
    };
  }
  
  if (debugEvent.type === 'PERFORMANCE') {
    enhancedData = {
      ...enhancedData,
      type: debugEvent.data?.type || 'metric',
      componentName: debugEvent.data?.componentName || 'unknown',
      renderTime: debugEvent.data?.renderTime || 0,
      memoryUsage: debugEvent.data?.memoryUsage || 0,
      timestamp: debugEvent.data?.timestamp || debugEvent.timestamp
    };
  }

  return {
    id: debugEvent.id,
    timestamp: debugEvent.timestamp,
    type: eventType,
    title: debugEvent.title,
    description: debugEvent.description,
    isError: debugEvent.isError,
    severity: debugEvent.severity,
    isBookmarked: debugEvent.isBookmarked,
    suggestion: debugEvent.suggestion,
    stateChanges: debugEvent.stateChanges,
    clientId: debugEvent.clientId,
    data: enhancedData,
    memorySize: debugEvent.memorySize,
    isCompressed: debugEvent.isCompressed,
    originalDataSize: debugEvent.originalDataSize,
  };
};

// 헬퍼 함수들
const extractComponentName = (title: string): string | null => {
  const patterns = [
    /State change in (\w+)/i,
    /(\w+Component|\w+View|\w+Page)\b/i,
    /Component\s+(\w+)/i,
    /(\w+):\s*User Action/i
  ];
  
  for (const pattern of patterns) {
    const match = title.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

const extractActionType = (title: string): string => {
  const actionPatterns = [
    { pattern: /click/i, action: 'click' },
    { pattern: /submit/i, action: 'submit' },
    { pattern: /input|type/i, action: 'input' },
    { pattern: /scroll/i, action: 'scroll' },
    { pattern: /navigate/i, action: 'navigation' },
    { pattern: /increment|increase/i, action: 'increment' },
    { pattern: /decrement|decrease/i, action: 'decrement' }
  ];
  
  for (const { pattern, action } of actionPatterns) {
    if (pattern.test(title)) {
      return action;
    }
  }
  return 'unknown';
};

/**
 * 서버 연결만 담당하는 내부 훅
 * 외부에서 직접 사용하지 말고, useDebugTimelineMachine에서만 사용
 */
export function useServerConnection(): UseServerConnectionReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [connectedClients, setConnectedClients] = useState<ConnectedClient[]>([]);
  const [internalEvents, setInternalEvents] = useState<DebugEvent[]>([]);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [, setLastCleanupTime] = useState<number>(Date.now());
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const eventBatchRef = useRef<DebugEvent[]>([]);
  const batchTimeoutRef = useRef<number | null>(null);
  const isConnectingRef = useRef(false);
  
  const maxReconnectAttempts = 10;
  const reconnectInterval = 3000;

  // ITimelineEvent로 변환된 events
  const events = internalEvents.map(convertToTimelineEvent);

  // 디버그 로그 추가
  console.log('🔍 useServerConnection Debug:', {
    isConnected,
    internalEventsCount: internalEvents.length,
    eventsCount: events.length,
    connectedClientsCount: connectedClients.length,
    connectionError,
    wsState: wsRef.current?.readyState,
  });

  // 메모리 통계 계산
  const memoryStats = calculateMemoryStats(events);

  // 배치 처리로 이벤트 추가
  const addEventsToBatch = useCallback((newEvents: DebugEvent[]) => {
    eventBatchRef.current.push(...newEvents);
    
    if (eventBatchRef.current.length >= MEMORY_CONSTANTS.BATCH_SIZE) {
      processBatch();
    } else if (!batchTimeoutRef.current) {
      batchTimeoutRef.current = window.setTimeout(() => {
        processBatch();
      }, 1000);
    }
  }, []);

  const processBatch = useCallback(() => {
    if (eventBatchRef.current.length === 0) return;
    
    const eventsToProcess = [...eventBatchRef.current];
    eventBatchRef.current = [];
    
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
      batchTimeoutRef.current = null;
    }

    setInternalEvents(prev => {
      const processedEvents = eventsToProcess.map(event => {
        const timelineEvent = convertToTimelineEvent(event);
        const compressed = compressEventData(timelineEvent);
        
        return {
          ...event,
          data: compressed.data,
          memorySize: compressed.memorySize,
          isCompressed: compressed.isCompressed,
          originalDataSize: compressed.originalDataSize
        };
      });

      const newEvents = [...prev, ...processedEvents];
      
      // 자동 정리
      if (newEvents.length > MEMORY_CONSTANTS.CLEANUP_THRESHOLD) {
        console.log('🧹 Auto cleanup triggered:', newEvents.length, 'events');
        setLastCleanupTime(Date.now());
        
        const timelineEvents = newEvents.map(convertToTimelineEvent);
        const cleaned = smartCleanup(timelineEvents);
        
        return cleaned.map(e => ({
          ...e,
          type: e.type === 'state_change' ? 'STATE_CHANGE' :
                e.type === 'user_action' ? 'USER_ACTION' :
                e.type === 'error' ? 'ERROR' :
                e.type === 'api_call' ? 'API_CALL' :
                e.type === 'connection' ? 'CONNECTION' :
                e.type === 'navigation' ? 'NAVIGATION' :
                e.type === 'performance' ? 'PERFORMANCE' : 'USER_ACTION'
        })) as DebugEvent[];
      }
      
      return newEvents;
    });
  }, []);

  // 이벤트 제목과 설명 생성
  const getEventTitle = (type: string, payload: any): string => {
    switch (type) {
      case 'USER_ACTION':
        return `${payload.actionType || 'Action'}: ${payload.element || 'element'}`;
      case 'STATE_CHANGE':
        return `State Changed: ${payload.componentName || 'component'}`;
      case 'ERROR':
        return `Error: ${payload.message || 'Unknown error'}`;
      case 'API_CALL':
        const status = payload.success === false ? ' (Failed)' : payload.status >= 400 ? ' (Error)' : '';
        return `API ${payload.method || 'GET'}: ${payload.url || 'request'}${status}`;
      case 'NAVIGATION':
        return `Navigation: ${payload.to || 'page'}`;
      case 'PERFORMANCE':
        return `Performance: ${payload.type || 'metric'} - ${payload.componentName || 'component'}`;
      default:
        return `Event: ${type}`;
    }
  };

  const getEventDescription = (type: string, payload: any): string => {
    switch (type) {
      case 'USER_ACTION':
        return `User performed ${payload.actionType} action on ${payload.element}`;
      case 'STATE_CHANGE':
        return `Component ${payload.componentName} state was updated`;
      case 'ERROR':
        return payload.message || 'An error occurred';
      case 'API_CALL':
        const duration = payload.duration ? ` (${payload.duration}ms)` : '';
        const status = payload.status ? ` - ${payload.status}` : '';
        return `${payload.method || 'GET'} ${payload.url}${status}${duration}`;
      case 'NAVIGATION':
        return `Navigated from ${payload.from || 'unknown'} to ${payload.to || 'unknown'}`;
      case 'PERFORMANCE':
        const metrics = [];
        if (payload.renderTime) metrics.push(`${payload.renderTime}ms render`);
        if (payload.memoryUsage) metrics.push(`${(payload.memoryUsage / 1024 / 1024).toFixed(1)}MB memory`);
        return `Performance metrics for ${payload.componentName}: ${metrics.join(', ')}`;
      default:
        return `Debug event: ${type}`;
    }
  };

  const handleDebugEvent = useCallback((type: string, payload: any, clientId: string, timestamp: number) => {
    if (clientId || payload?.clientId) {
      const eventClientId = clientId || payload.clientId;
      const newEvent: DebugEvent = {
        id: `${eventClientId}-${timestamp}-${Math.random().toString(36).substring(2, 9)}`,
        type: type as DebugEvent['type'],
        timestamp,
        clientId: eventClientId,
        data: payload,
        title: getEventTitle(type, payload),
        description: getEventDescription(type, payload),
        isError: type === 'ERROR',
        severity: type === 'ERROR' ? 'critical' : 
                 type === 'API_CALL' && payload.success === false ? 'warning' :
                 'info'
      };
      
      addEventsToBatch([newEvent]);
      
      // 클라이언트 활동 시간 업데이트
      setConnectedClients(prev => 
        prev.map(client => 
          client.id === eventClientId 
            ? { ...client, lastActivity: new Date() }
            : client
        )
      );
    }
  }, [addEventsToBatch]);

  const handleServerMessage = useCallback((message: any) => {
    const { type, payload, timestamp, clientId } = message;
    
    switch (type) {
      case 'CONNECTION':
        if (payload?.type === 'welcome') {
          console.log('✅ Welcome message received from server');
        }
        break;
        
      case 'client_connected':
      case 'dashboard_ready':
        if (payload?.clientId || clientId) {
          const newClientId = payload?.clientId || clientId;
          const newClient: ConnectedClient = {
            id: newClientId,
            connectedAt: new Date(),
            lastActivity: new Date(),
            url: payload?.url,
            userAgent: payload?.userAgent
          };
          
          setConnectedClients(prev => {
            const exists = prev.find(c => c.id === newClientId);
            if (exists) return prev;
            return [...prev, newClient];
          });
        }
        break;
        
      case 'client_disconnected':
        if (payload?.clientId || clientId) {
          const disconnectedClientId = payload?.clientId || clientId;
          setConnectedClients(prev => prev.filter(c => c.id !== disconnectedClientId));
        }
        break;
        
      case 'USER_ACTION':
      case 'STATE_CHANGE':
      case 'ERROR':
      case 'API_CALL':
      case 'NAVIGATION':
      case 'PERFORMANCE':
        handleDebugEvent(type, payload, clientId, timestamp);
        break;
        
      default:
        if (payload && (clientId || payload.clientId)) {
          handleDebugEvent(type, payload, clientId || payload.clientId, timestamp);
        }
    }
  }, [handleDebugEvent]);

  const connect = useCallback(() => {
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    
    try {
      isConnectingRef.current = true;
      
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      wsRef.current = new WebSocket(WEBSOCKET_URL);
      
      wsRef.current.onopen = () => {
        isConnectingRef.current = false;
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;
        
        setTimeout(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            const message = {
              type: 'CONNECTION',
              payload: {
                type: 'dashboard_ready',
                timestamp: Date.now()
              },
              timestamp: Date.now()
            };
            
            try {
              wsRef.current.send(JSON.stringify(message));
            } catch (sendError) {
              console.error('❌ Failed to send dashboard ready message:', sendError);
            }
          }
        }, 100);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleServerMessage(message);
        } catch (error) {
          console.error('❌ Failed to parse server message:', error);
        }
      };
      
      wsRef.current.onclose = (event) => {
        isConnectingRef.current = false;
        setIsConnected(false);
        
        if (event.code === 1000 || event.code === 1001) {
          return;
        }
        
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectInterval);
        } else {
          setConnectionError('Maximum reconnection attempts reached');
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('🔥 WebSocket error:', error);
        isConnectingRef.current = false;
        setConnectionError('WebSocket connection error');
      };
      
    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      isConnectingRef.current = false;
      setConnectionError('Failed to create connection');
    }
  }, [handleServerMessage]);

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    reconnectAttemptsRef.current = 0;
    setConnectionError(null);
    connect();
  }, [connect]);

  const clearEvents = useCallback(() => {
    setInternalEvents([]);
    setLastCleanupTime(Date.now());
    
    eventBatchRef.current = [];
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
      batchTimeoutRef.current = null;
    }
  }, []);

  const updateEvents = useCallback((newEvents: ITimelineEvent[]) => {
    const debugEvents: DebugEvent[] = newEvents.map(e => ({
      ...e,
      type: e.type === 'state_change' ? 'STATE_CHANGE' :
            e.type === 'user_action' ? 'USER_ACTION' :
            e.type === 'error' ? 'ERROR' :
            e.type === 'api_call' ? 'API_CALL' :
            e.type === 'connection' ? 'CONNECTION' :
            e.type === 'navigation' ? 'NAVIGATION' :
            e.type === 'performance' ? 'PERFORMANCE' : 'USER_ACTION'
    })) as DebugEvent[];
    
    setInternalEvents(debugEvents);
  }, []);

  useEffect(() => {
    let isConnecting = true;
    
    const connectTimer = setTimeout(() => {
      if (isConnecting) {
        connect();
      }
    }, 100);
    
    return () => {
      isConnecting = false;
      clearTimeout(connectTimer);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
      
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component cleanup');
        wsRef.current = null;
      }
    };
  }, [connect]);

  return {
    isConnected,
    connectedClients,
    events,
    connectionError,
    memoryStats,
    reconnect,
    clearEvents,
    updateEvents
  };
}
